import React, { useState, useEffect } from 'react';
import { View } from 'react-native';
import { useQuery, useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';

import { CLIENTS } from 'app/src/components/Clients/queries';
import { SUGGEST_MEDICINES } from 'app/src/components/Medicines/queries';
import {
  Container,
  ButtonContainer,
  EmojiText,
  LabelText,
} from 'app/src/elements/ButtonPicker';
import { Fields, defaultValues, prepareInput } from 'app/src/elements/forms';
import { Button } from 'app/src/elements/buttons';
import { Loading } from 'app/src/elements/DataState';
import { Title, Subtitle } from 'app/src/styles';
import { useNavigate } from 'app/src/utils/routing';

import { CREATE_MEDICINE_GROUP } from './queries';

export const FIELDS = {
  clientId: {
    label: 'Client',
    type: 'hidden',
    rules: { required: true },
  },
  name: {
    label: 'Name',
    rules: { required: true },
  },
  scheduledTime: {
    label: 'Scheduled Time',
    hint: 'e.g. Morning, 8:00 AM',
  },
};

const NewMedicineGroup = ({ onCreated }) => {
  const [formStatus, setFormStatus] = useState();
  const [selectedMedicineIds, setSelectedMedicineIds] = useState([]);
  const navigate = useNavigate();

  const { control, handleSubmit, formState: { errors }, watch } = useForm({
    defaultValues: defaultValues({ fields: FIELDS }),
  });

  const clientId = watch('clientId') || null;

  const { data: clientsData } = useQuery(CLIENTS, {
    variables: { limit: 100000, offset: 'MA==', options: {} },
  });

  const { data: medicinesData } = useQuery(SUGGEST_MEDICINES, {
    variables: { clientId, all: true },
    skip: !clientId,
  });

  const [createMedicineGroup] = useMutation(CREATE_MEDICINE_GROUP, {
    onCompleted: data => {
      if (onCreated) {
        onCreated(data.createMedicineGroup.medicineGroup);
      } else {
        navigate('/medicine-groups');
      }
    },
  });

  const medicines = medicinesData?.medicines || [];

  const toggleMedicine = medicineId => {
    setSelectedMedicineIds(prev =>
      prev.includes(medicineId)
        ? prev.filter(id => id !== medicineId)
        : [...prev, medicineId]
    );
  };

  const onSubmit = input => {
    setFormStatus('submitting');
    createMedicineGroup({
      variables: {
        input: {
          ...prepareInput(input, FIELDS),
          medicineGroupMedicinesAttributes: selectedMedicineIds.map(
            medicineId => ({ medicineId })
          ),
        },
      },
    });
  };

  const fields = { ...FIELDS };

  if (clientsData) {
    fields.clientId = {
      ...fields.clientId,
      type: 'select',
      select: [
        { value: '', label: '--Choose--' },
        ...clientsData.clients.edges.map(edge => ({
          value: edge.node.id,
          label: edge.node.name,
        })),
      ],
    };
  }

  return (
    <View>
      <Title>Add Medicine Group</Title>

      <Fields fields={fields} control={control} errors={errors} />

      {clientId && (
        <>
          <Subtitle>Medicines</Subtitle>
          <Container>
            {medicines.map(medicine => (
              <ButtonContainer
                key={medicine.id}
                onPress={() => toggleMedicine(medicine.id)}
                notSelected={!selectedMedicineIds.includes(medicine.id)}
              >
                <EmojiText>{medicine.emoji || medicine.name[0]}</EmojiText>
                <LabelText>{medicine.name}</LabelText>
              </ButtonContainer>
            ))}
          </Container>
        </>
      )}

      {'submitting' === formStatus ? (
        <Loading />
      ) : (
        <Button label="Save" onPress={handleSubmit(onSubmit)} wide />
      )}
    </View>
  );
};

export default NewMedicineGroup;
